import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { sanitizeHtml } from "@/lib/sanitize";
import { useGetStarted } from "@/components/GetStartedDialog";
import { CheckCircle2, Mail, Inbox } from "lucide-react";

/**
 * Confirmation page — every successful form submission (Get Started, Audit,
 * etc.) lands here so Google Ads / GA / Meta pixels can be triggered by a
 * dedicated URL destination.
 *
 * Query params:
 *   ?source=contact  — Get Started lead form
 *   ?source=audit    — Site Audit lead form
 *   ?source=<other>  — sensible generic copy
 */
const COPY = {
  contact: {
    eyebrow: "§ Message received",
    h1_prefix: "Thanks.",
    h1_suffix: "I&rsquo;ll be in touch.",
    intro:
      "I&rsquo;ve got your inquiry and I&rsquo;ll read it properly — not skim it. You&rsquo;ll hear from me personally within 1–2 business days.",
    steps: [
      {
        n: "01",
        title: "A personal reply within 1–2 business days.",
        body: "From me, Jeremy — not a form-letter. I'll answer your specific questions and confirm whether we're a fit.",
      },
      {
        n: "02",
        title: "A short discovery call, if we're a good fit.",
        body: "20–30 minutes to understand your business, your audience, and what \u201cdone\u201d looks like for you. Zero pressure.",
      },
      {
        n: "03",
        title: "A written proposal with scope, timeline, and price.",
        body: "Fixed scope, fixed price. No surprises. If we move forward, you'll know exactly what you're getting and when.",
      },
    ],
    primary: { label: "Back to home", to: "/" },
    secondary: { label: "Browse selected work", to: "/work" },
  },
  audit: {
    eyebrow: "§ Audit received · Report is on its way",
    h1_prefix: "Your audit is",
    h1_suffix: "in your inbox.",
    intro:
      "The full report — including the top problems to fix first and every finding by category — is on its way to your email now. If you don&rsquo;t see it in the next minute or two, check your spam folder just in case.",
    steps: [
      {
        n: "01",
        title: "Read the report.",
        body: "It shows the top critical issues with plain-language business consequences, followed by every finding grouped by Accessibility, Performance, Mobile, and Security.",
      },
      {
        n: "02",
        title: "Reply if you'd like us to fix any of it.",
        body: "Redkey Web Design remediates accessibility, performance, mobile, and security issues for U.S. businesses every day. Fixed price, fixed timeline, no lock-in.",
      },
      {
        n: "03",
        title: "Otherwise, keep it for reference.",
        body: "It's yours. Forward to your team, save it for later, or use it as a starting checklist. No follow-up spam — you have my word.",
      },
    ],
    primary: { label: "Have Redkey fix these problems", cta: true },
    secondary: { label: "Back to home", to: "/" },
  },
  default: {
    eyebrow: "§ Submitted",
    h1_prefix: "Thanks —",
    h1_suffix: "we&rsquo;re on it.",
    intro:
      "Your submission has been received. We&rsquo;ll follow up shortly at the email address you provided.",
    steps: [],
    primary: { label: "Back to home", to: "/" },
    secondary: { label: "Selected work", to: "/work" },
  },
};

export default function Confirmation() {
  const [params] = useSearchParams();
  const source = (params.get("source") || "default").toLowerCase();
  const copy = COPY[source] || COPY.default;
  const { open: openGetStarted } = useGetStarted();

  // Nudge to the top on entry (form dialogs sometimes leave scroll offset)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Fire the Google Ads conversion event (in addition to the URL-based
  // conversion tracked by /confirmation). Which label to send is chosen
  // by the ?source= query param and pulled from env vars so you can
  // paste the label from Google Ads without any code change.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const sendTo = source === "audit"
      ? process.env.REACT_APP_ADS_CONVERSION_AUDIT
      : process.env.REACT_APP_ADS_CONVERSION_CONTACT;
    if (!sendTo) return; // no label configured yet — silently skip
    try {
      window.gtag("event", "conversion", {
        send_to: sendTo,
        event_callback: () => { /* no-op */ },
      });
      // Also fire a GA4 custom event so you can build funnels in GA4
      window.gtag("event", `${source}_form_submit`, {
        event_category: "engagement",
        event_label: source,
      });
    } catch (e) {
      // Ad blockers or gtag not ready — never break the confirmation UX
      if (typeof console !== "undefined") {
        console.warn("[gtag] conversion event failed:", e);
      }
    }
  }, [source]);

  return (
    <div data-testid="confirmation-page" data-source={source}>
      <PageHead
        title="Thanks — Redkey Web Design"
        description="Your submission has been received. We'll be in touch shortly."
        canonical="https://redkeyweb.design/confirmation"
        /* Confirmation pages should never rank */
        robots="noindex, nofollow"
      />

      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-10 pt-20 md:pt-28 pb-8">
        <div className="flex items-start gap-5 md:gap-6">
          <div
            aria-hidden
            className="shrink-0 mt-2 h-12 w-12 md:h-14 md:w-14 rounded-full bg-brand-red text-paper flex items-center justify-center"
          >
            <CheckCircle2 size={26} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1
              data-testid="confirmation-title"
              className="font-display text-[44px] sm:text-6xl md:text-[86px] leading-[0.98] mt-4 max-w-[16ch]"
            >
              {copy.h1_prefix}{" "}
              <em
                className="not-italic text-brand-red"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(copy.h1_suffix) }}
              />
            </h1>
            <p
              className="mt-6 max-w-[62ch] text-lg text-ink-soft leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(copy.intro) }}
            />
          </div>
        </div>
      </section>

      {/* Inbox hint for audit users */}
      {source === "audit" && (
        <section className="mx-auto max-w-[1200px] px-6 md:px-10 pb-4">
          <div
            className="border hairline-strong bg-paper-alt px-6 py-5 md:px-8 md:py-6 flex items-start gap-4"
            data-testid="confirmation-inbox-hint"
          >
            <Inbox size={20} strokeWidth={1.75} className="text-ink mt-1 shrink-0" aria-hidden />
            <p className="font-body text-ink-soft leading-relaxed">
              <strong className="font-ui text-ink text-sm">Where to look.</strong>{" "}
              Look for &ldquo;<strong className="text-ink">Your Redkey site audit</strong>&rdquo;
              from{" "}
              <span className="text-ink font-ui text-sm">hello@redkeyweb.design</span>.
              If it doesn&rsquo;t arrive in a couple of minutes, check your promotions
              or spam folder — and add us to your contacts so future reports land in the inbox.
            </p>
          </div>
        </section>
      )}

      {/* Steps */}
      {copy.steps.length > 0 && (
        <section className="mx-auto max-w-[1200px] px-6 md:px-10 border-t hairline mt-10 pt-12 pb-10">
          <p className="eyebrow">§ What happens next</p>
          <ol className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {copy.steps.map((s) => (
              <li key={s.n} data-testid={`confirmation-step-${s.n}`}>
                <p className="font-display text-3xl md:text-4xl text-brand-red">{s.n}</p>
                <p className="mt-3 font-ui text-ink text-base leading-snug">
                  {s.title}
                </p>
                <p className="mt-2 font-body text-ink-soft leading-relaxed">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* CTAs */}
      <section className="mx-auto max-w-[1200px] px-6 md:px-10 pt-6 pb-24">
        <div className="flex flex-wrap gap-3">
          {copy.primary?.cta ? (
            <button
              type="button"
              onClick={openGetStarted}
              data-testid="confirmation-cta-primary"
              className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors font-ui"
            >
              {copy.primary.label} <span aria-hidden>→</span>
            </button>
          ) : (
            <Link
              to={copy.primary.to || "/"}
              data-testid="confirmation-cta-primary"
              className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors font-ui"
            >
              {copy.primary.label} <span aria-hidden>→</span>
            </Link>
          )}
          {copy.secondary && (
            <Link
              to={copy.secondary.to || "/"}
              data-testid="confirmation-cta-secondary"
              className="inline-flex items-center gap-3 border hairline-strong text-ink px-6 py-4 hover:border-ink transition-colors font-ui"
            >
              {copy.secondary.label}
            </Link>
          )}
        </div>
        <p className="mt-8 font-ui text-[11px] tracking-widest uppercase text-label flex items-center gap-2">
          <Mail size={12} aria-hidden /> No drip campaigns · No sharing · Just a real reply
        </p>
      </section>
    </div>
  );
}
